import React, { useState } from 'react';
import axios from 'axios';

// Subcomponent: Contact Info Item
const ContactInfoItem = ({ icon, title, children, bgColor = "bg-primary" }) => (
  <div className="flex items-start">
    <div className={`w-12 h-12 ${bgColor} rounded-full flex items-center justify-center mr-4 flex-shrink-0`}>
      {icon}
    </div>
    <div>
      <h3 className="font-bold text-primary mb-1">{title}</h3>
      {children}
    </div>
  </div>
);

// Subcomponent: Contact Information Section
const ContactInformation = () => (
  <div>
    <h2 className="text-2xl font-bold text-primary mb-6">Get in Touch</h2>
    
    <div className="space-y-6">
      <ContactInfoItem
        title="Email"
        icon={
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        }
      >
        <a href="mailto:info@vajreyaventures.com" className="text-gray-700 hover:text-primary transition-colors">
          info@vajreyaventures.com
        </a>
      </ContactInfoItem>

      <ContactInfoItem
        title="Location"
        icon={
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        }
      >
        <p className="text-gray-700">Bangalore, Karnataka, India</p>
      </ContactInfoItem>

      <ContactInfoItem
        title="Company"
        bgColor="bg-gold"
        icon={
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        }
      >
        <p className="text-gray-700">Vajreya Ventures Private Limited</p>
        <p className="text-sm text-gray-600">CEO: Mayoor Shastry BN</p>
        <p className="text-sm text-gray-600">GSTIN: 29AAKCV2897F1ZO</p>
      </ContactInfoItem>

      <ContactInfoItem
        title="Website"
        icon={
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
      >
        <p className="text-gray-700">vajreyaventures.com</p>
      </ContactInfoItem>
    </div>

    <div className="mt-8 bg-white border-2 gold-border rounded-lg p-6">
      <h3 className="font-bold text-primary mb-3">Business Hours</h3>
      <p className="text-gray-700 text-sm">Monday - Friday: 9:00 AM - 6:00 PM IST</p>
      <p className="text-gray-700 text-sm">Saturday: 10:00 AM - 2:00 PM IST</p>
      <p className="text-gray-700 text-sm">Sunday: Closed</p>
    </div>
  </div>
);

// Subcomponent: Form Input Field
const FormInput = ({ label, type = "text", name, value, onChange, required = false, placeholder }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-semibold text-gray-700 mb-2">
      {label} {required && '*'}
    </label>
    <input
      type={type}
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:border-primary focus:outline-none transition-colors"
      required={required}
      placeholder={placeholder}
    />
  </div>
);

// Subcomponent: Contact Form
const ContactForm = ({ formData, setFormData, submitting, onSubmit }) => {
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-primary mb-6">Send us a Message</h2>
      
      <form onSubmit={onSubmit} className="space-y-6">
        <FormInput
          label="Full Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          placeholder="Your name"
        />

        <FormInput
          label="Email Address"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          placeholder="your.email@example.com"
        />

        <FormInput
          label="Phone Number"
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder="+91 XXXXX XXXXX"
        />

        <FormInput
          label="Company Name"
          name="company"
          value={formData.company}
          onChange={handleChange}
          placeholder="Your company"
        />

        <div>
          <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows="6"
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:border-primary focus:outline-none transition-colors resize-none"
            required
            placeholder="Tell us about your inquiry..."
          ></textarea>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-primary text-white py-3 px-6 rounded-md hover:bg-primary-dark transition-colors font-semibold text-lg disabled:opacity-50"
        >
          {submitting ? 'Sending...' : 'Send Message'}
        </button>
      </form>
    </div>
  );
};

// Main Contact Component
const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${backendUrl}/api/contact`, formData);
      
      if (response.status === 200) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="content-section">
      <div className="container mx-auto px-4">
        <div className="glass-card rounded-lg p-8 md:p-12">
          <h1 className="text-4xl font-bold text-primary mb-6 text-center">
            Contact Us
          </h1>
          <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
          
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed text-center mb-8">
              Get in touch with Vajreya Ventures to discuss how we can help you achieve your business and investment goals.
            </p>

            {submitStatus === 'success' && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
                Thank you for your message! We'll get back to you soon.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                Something went wrong. Please try again.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <ContactInformation />
              <ContactForm 
                formData={formData}
                setFormData={setFormData}
                submitting={submitting}
                onSubmit={handleSubmit}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
